import { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Heading,
    Text,
    Flex,
    Button,
    IconButton,
    Icon,
    Spacer,
    Container,
    Badge,
    Table,
    Box,
    Stack,
    FileUpload,
    Tabs,
    Spinner,
    EmptyState,
} from '@chakra-ui/react';
import { useParams, useLoaderData, useNavigate } from 'react-router-dom';
import { KnowledgeBaseDocument, GeminiDocument, KnowledgeBaseDocumentStatus, ContentType } from '@/lib/types';
import {
    TrashIcon,
    Squares2X2Icon,
    ArrowUpTrayIcon,
    ArrowPathIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import api from '@/utils/api';
import { toaster } from '@/components/ui/toaster';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { Tooltip } from '@/components/ui/tooltip';

const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getStatusBadge = (status: KnowledgeBaseDocumentStatus) => {
    switch (status) {
        case 'ready':
            return (
                <Badge colorPalette="green" size="sm">
                    <Icon as={CheckCircleIcon} w={3} h={3} mr={1} />
                    Ready
                </Badge>
            );
        case 'processing':
            return (
                <Badge colorPalette="blue" size="sm">
                    <Spinner size="xs" mr={1} />
                    Processing
                </Badge>
            );
        case 'pending':
            return (
                <Badge colorPalette="yellow" size="sm">
                    <Icon as={ClockIcon} w={3} h={3} mr={1} />
                    Pending
                </Badge>
            );
        case 'error':
            return (
                <Badge colorPalette="red" size="sm">
                    <Icon as={ExclamationCircleIcon} w={3} h={3} mr={1} />
                    Error
                </Badge>
            );
        default:
            return (
                <Badge colorPalette="gray" size="sm">
                    Unknown
                </Badge>
            );
    }
};

interface DocumentRowProps {
    document: KnowledgeBaseDocument;
    onDelete: (doc: KnowledgeBaseDocument) => void;
    onRefreshStatus: (doc: KnowledgeBaseDocument) => void;
    canDelete: boolean;
    isRefreshing: boolean;
}

const DocumentTableRow = ({
    document,
    onDelete,
    onRefreshStatus,
    canDelete,
    isRefreshing
}: DocumentRowProps) => {
    return (
        <Table.Row>
            <Table.Cell>
                <Flex direction="column" gap={1}>
                    <Text
                        color="gray.700"
                        fontSize="sm"
                        fontWeight="medium"
                        maxW="300px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        title={document.original_filename}
                    >
                        {document.original_filename}
                    </Text>
                    <Text color="gray.400" fontSize="xs">
                        {document.mime_type}
                    </Text>
                </Flex>
            </Table.Cell>
            <Table.Cell>
                <Text color="gray.500" fontSize="sm">
                    {formatFileSize(document.file_size_bytes)}
                </Text>
            </Table.Cell>
            <Table.Cell>
                <Flex direction="column" gap={1}>
                    {getStatusBadge(document.status)}
                    {document.error_message && (
                        <Tooltip content={document.error_message}>
                            <Text color="red.500" fontSize="xs" cursor="help">
                                View error
                            </Text>
                        </Tooltip>
                    )}
                </Flex>
            </Table.Cell>
            <Table.Cell>
                <Text color="gray.400" fontSize="sm">
                    {formatDate(document.created_at)}
                </Text>
            </Table.Cell>
            <Table.Cell>
                <Flex gap={2} align="center">
                    {(document.status === 'pending' || document.status === 'processing') && (
                        <Tooltip content="Refresh Status">
                            <IconButton
                                aria-label="Refresh Status"
                                variant="ghost"
                                size="xs"
                                onClick={() => onRefreshStatus(document)}
                                loading={isRefreshing}
                            >
                                <Icon as={ArrowPathIcon} w={4} h={4} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip content="Delete">
                            <IconButton
                                aria-label="Delete"
                                variant="ghost"
                                size="xs"
                                colorPalette="red"
                                onClick={() => onDelete(document)}
                            >
                                <Icon as={TrashIcon} w={4} h={4} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Flex>
            </Table.Cell>
        </Table.Row>
    );
};

const MobileDocumentCard = ({
    document,
    onDelete,
    onRefreshStatus,
    canDelete,
    isRefreshing
}: DocumentRowProps) => {
    return (
        <Card.Root>
            <Card.Body p={4}>
                <Stack gap={3}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                        <Flex direction="column" gap={1} flex={1}>
                            <Text
                                color="gray.700"
                                fontSize="md"
                                fontWeight="medium"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                                title={document.original_filename}
                            >
                                {document.original_filename}
                            </Text>
                            <Text color="gray.400" fontSize="xs">
                                {document.mime_type}
                            </Text>
                        </Flex>
                        {getStatusBadge(document.status)}
                    </Flex>

                    {document.error_message && (
                        <Text color="red.500" fontSize="xs">
                            Error: {document.error_message}
                        </Text>
                    )}

                    <Flex justify="space-between" align="center" gap={2}>
                        <Text color="gray.500" fontSize="sm">
                            {formatFileSize(document.file_size_bytes)}
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                            {formatDate(document.created_at)}
                        </Text>
                    </Flex>

                    <Flex gap={3} wrap="wrap">
                        {(document.status === 'pending' || document.status === 'processing') && (
                            <Tooltip content="Refresh Status">
                                <IconButton
                                    aria-label="Refresh Status"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onRefreshStatus(document)}
                                    loading={isRefreshing}
                                >
                                    <Icon as={ArrowPathIcon} w={5} h={5} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canDelete && (
                            <Tooltip content="Delete">
                                <IconButton
                                    aria-label="Delete"
                                    variant="ghost"
                                    size="sm"
                                    colorPalette="red"
                                    onClick={() => onDelete(document)}
                                >
                                    <Icon as={TrashIcon} w={5} h={5} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Flex>
                </Stack>
            </Card.Body>
        </Card.Root>
    );
};

interface GeminiDocumentRowProps {
    document: GeminiDocument;
    onDelete: (doc: GeminiDocument) => void;
    canDelete: boolean;
}

const getGeminiStateBadge = (state?: string) => {
    const isActive = state?.includes('ACTIVE');
    const displayState = state?.replace('STATE_', '') || 'Unknown';

    return (
        <Badge
            colorPalette={isActive ? 'green' : 'gray'}
            size="sm"
        >
            {displayState}
        </Badge>
    );
};

const GeminiDocumentTableRow = ({ document, onDelete, canDelete }: GeminiDocumentRowProps) => {
    const displayName = document.displayName || document.name?.split('/').pop() || 'Unknown';

    return (
        <Table.Row>
            <Table.Cell>
                <Flex direction="column" gap={1}>
                    <Text
                        color="gray.700"
                        fontSize="sm"
                        fontWeight="medium"
                        maxW="300px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        title={displayName}
                    >
                        {displayName}
                    </Text>
                    <Text color="gray.400" fontSize="xs" fontFamily="mono">
                        {document.name}
                    </Text>
                </Flex>
            </Table.Cell>
            <Table.Cell>
                <Text color="gray.500" fontSize="sm">
                    {document.sizeBytes ? formatFileSize(parseInt(document.sizeBytes)) : '-'}
                </Text>
            </Table.Cell>
            <Table.Cell>
                {getGeminiStateBadge(document.state)}
            </Table.Cell>
            <Table.Cell>
                <Text color="gray.400" fontSize="sm">
                    {document.createTime ? formatDate(document.createTime) : '-'}
                </Text>
            </Table.Cell>
            <Table.Cell>
                {canDelete && (
                    <Tooltip content="Delete from Gemini">
                        <IconButton
                            aria-label="Delete from Gemini"
                            variant="ghost"
                            size="xs"
                            colorPalette="red"
                            onClick={() => onDelete(document)}
                        >
                            <Icon as={TrashIcon} w={4} h={4} />
                        </IconButton>
                    </Tooltip>
                )}
            </Table.Cell>
        </Table.Row>
    );
};

const MobileGeminiCard = ({ document, onDelete, canDelete }: GeminiDocumentRowProps) => {
    const displayName = document.displayName || document.name?.split('/').pop() || 'Unknown';

    return (
        <Card.Root>
            <Card.Body p={4}>
                <Stack gap={3}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                        <Flex direction="column" gap={1} flex={1}>
                            <Text
                                color="gray.700"
                                fontSize="md"
                                fontWeight="medium"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                            >
                                {displayName}
                            </Text>
                            <Text color="gray.400" fontSize="xs" fontFamily="mono">
                                {document.name}
                            </Text>
                        </Flex>
                        {getGeminiStateBadge(document.state)}
                    </Flex>

                    <Flex justify="space-between" align="center">
                        <Text color="gray.500" fontSize="sm">
                            {document.sizeBytes ? formatFileSize(parseInt(document.sizeBytes)) : '-'}
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                            {document.createTime ? formatDate(document.createTime) : '-'}
                        </Text>
                    </Flex>

                    {canDelete && (
                        <Flex>
                            <Tooltip content="Delete from Gemini">
                                <IconButton
                                    aria-label="Delete from Gemini"
                                    variant="ghost"
                                    size="sm"
                                    colorPalette="red"
                                    onClick={() => onDelete(document)}
                                >
                                    <Icon as={TrashIcon} w={5} h={5} />
                                </IconButton>
                            </Tooltip>
                        </Flex>
                    )}
                </Stack>
            </Card.Body>
        </Card.Root>
    );
};

export default function KnowledgeBase() {
    const isAdmin = useIsInAdminGroup();
    const isCancelled = useIsCancelled();
    const initialDocuments = useLoaderData() as KnowledgeBaseDocument[];
    const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>(initialDocuments || []);
    const [filteredDocuments, setFilteredDocuments] = useState<KnowledgeBaseDocument[]>([]);
    const [geminiDocuments, setGeminiDocuments] = useState<GeminiDocument[]>([]);
    const [isLoadingGemini, setIsLoadingGemini] = useState(false);
    const [geminiStoreExists, setGeminiStoreExists] = useState<boolean | null>(null);
    const [contentType, setContentType] = useState<ContentType | null>(null);
    const navigate = useNavigate();
    const { workspaceId, contentTypeId } = useParams();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<KnowledgeBaseDocument | null>(null);

    const [showGeminiDeleteModal, setShowGeminiDeleteModal] = useState(false);
    const [isDeletingGemini, setIsDeletingGemini] = useState(false);
    const [geminiDocToDelete, setGeminiDocToDelete] = useState<GeminiDocument | null>(null);

    const [refreshingDocId, setRefreshingDocId] = useState<number | null>(null);
    const [isRefreshingDocuments, setIsRefreshingDocuments] = useState(false);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('documents');

    const canModify = isAdmin && !isCancelled;

    // Fetch content type info
    useEffect(() => {
        async function fetchContentType() {
            if (!contentTypeId) return;

            try {
                const response = await api.get(`content-types/${contentTypeId}`);
                setContentType(response.data.data);
            } catch (err: any) {
                console.error('Failed to fetch content type:', err);
            }
        }

        fetchContentType();
    }, [contentTypeId]);

    // Sort and filter documents
    useEffect(() => {
        const sorted = [...documents].sort((a, b) => {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setFilteredDocuments(sorted);
    }, [documents]);

    // Fetch/refresh all documents
    const fetchDocuments = useCallback(async () => {
        if (!workspaceId || !contentTypeId) return;

        setIsRefreshingDocuments(true);
        try {
            const url = `/knowledge-base/gemini/${workspaceId}/documents?content_type_id=${contentTypeId}`;
            const response = await api.get(url);
            const docs = response.data?.documents || [];
            setDocuments(docs);
        } catch (error: any) {
            toaster.create({
                type: 'error',
                title: error.response?.data?.error || 'Failed to refresh documents',
            });
        } finally {
            setIsRefreshingDocuments(false);
        }
    }, [workspaceId, contentTypeId]);

    // Fetch Gemini documents when tab changes
    const fetchGeminiDocuments = useCallback(async () => {
        if (!workspaceId || !contentTypeId) return;

        setIsLoadingGemini(true);
        try {
            const url = `/knowledge-base/gemini/${workspaceId}/gemini-documents?content_type_id=${contentTypeId}`;
            const response = await api.get(url);

            // Handle different possible response structures
            const docs = response.data?.documents || response.data?.data || response.data || [];
            setGeminiDocuments(Array.isArray(docs) ? docs : []);
            setGeminiStoreExists(true);
        } catch (error: any) {
            console.error('Error fetching Gemini documents:', error);
            const errorMessage = error.response?.data?.error || '';

            // Only treat as "store not found" if it's specifically about store not existing
            if (
                errorMessage.toLowerCase().includes('store not found') ||
                errorMessage.toLowerCase().includes('file search store not found')
            ) {
                setGeminiStoreExists(false);
                setGeminiDocuments([]);
            } else {
                setGeminiStoreExists(true);
                toaster.create({
                    type: 'error',
                    title: errorMessage || 'Failed to fetch Gemini documents',
                });
            }
        } finally {
            setIsLoadingGemini(false);
        }
    }, [workspaceId, contentTypeId]);

    useEffect(() => {
        if (activeTab === 'gemini') {
            fetchGeminiDocuments();
        }
    }, [activeTab, fetchGeminiDocuments]);

    // Handle file upload
    const handleUploadAccept = (details: any) => {
        if (details && details.files.length > 0) {
            setUploadFiles((prev) => [...prev, ...details.files]);
        }
    };

    const handleUploadSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!workspaceId || uploadFiles.length === 0) return;

        setIsUploading(true);
        try {
            for (const file of uploadFiles) {
                const formData = new FormData();
                formData.append('file', file);
                if (contentTypeId) {
                    formData.append('content_type_id', contentTypeId);
                }

                const response = await api.post(
                    `/knowledge-base/gemini/${workspaceId}/documents`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data?.document) {
                    setDocuments(prev => [response.data.document, ...prev]);
                    // Store is now created if it wasn't before
                    setGeminiStoreExists(true);
                }
            }

            toaster.create({ title: 'Documents uploaded successfully', type: 'success' });
            setUploadFiles([]);
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to upload documents'
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Handle document status refresh
    const handleRefreshStatus = async (doc: KnowledgeBaseDocument) => {
        setRefreshingDocId(doc.id);
        try {
            const response = await api.get(`/knowledge-base/gemini/documents/${doc.id}/status`);
            if (response.data?.document) {
                setDocuments(prev =>
                    prev.map(d => d.id === doc.id ? response.data.document : d)
                );
            }
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to refresh status',
            });
        } finally {
            setRefreshingDocId(null);
        }
    };

    // Handle document deletion
    const handleDelete = (doc: KnowledgeBaseDocument) => {
        setDocumentToDelete(doc);
        setShowDeleteModal(true);
    };

    const onConfirmDelete = async () => {
        if (!documentToDelete) return;

        setIsDeleting(true);
        try {
            await api.delete(`/knowledge-base/gemini/documents/${documentToDelete.id}`);
            setDocuments(prev => prev.filter(d => d.id !== documentToDelete.id));
            toaster.create({ title: 'Document deleted', type: 'success' });
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to delete document',
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setDocumentToDelete(null);
        }
    };

    // Handle Gemini document deletion
    const handleGeminiDelete = (doc: GeminiDocument) => {
        setGeminiDocToDelete(doc);
        setShowGeminiDeleteModal(true);
    };

    const onConfirmGeminiDelete = async () => {
        if (!geminiDocToDelete || !workspaceId || !contentTypeId) return;

        setIsDeletingGemini(true);
        try {
            const url = `/knowledge-base/gemini/${workspaceId}/gemini-documents?document_name=${encodeURIComponent(geminiDocToDelete.name)}&content_type_id=${contentTypeId}`;
            await api.delete(url);
            setGeminiDocuments(prev => prev.filter(d => d.name !== geminiDocToDelete.name));
            toaster.create({ title: 'Document deleted from Gemini', type: 'success' });
        } catch (err: any) {
            toaster.create({
                type: 'error',
                title: err?.response?.data?.error || 'Failed to delete document from Gemini',
            });
        } finally {
            setIsDeletingGemini(false);
            setShowGeminiDeleteModal(false);
            setGeminiDocToDelete(null);
        }
    };

    // Handle search
    const handleSearch = (search: string) => {
        if (!search) {
            const sorted = [...documents].sort((a, b) => {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
            setFilteredDocuments(sorted);
            return;
        }

        const filtered = documents.filter((doc) => {
            return (
                doc.original_filename?.toLowerCase().includes(search.toLowerCase()) ||
                doc.document_name?.toLowerCase().includes(search.toLowerCase())
            );
        });

        const sorted = [...filtered].sort((a, b) => {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setFilteredDocuments(sorted);
    };

    return (
        <WorkspaceLayout>
            <Card.Root minH="100vh" mx="auto" width={{ md: '90%' }} maxW="6xl">
                <Card.Header>
                    <Container maxW="4xl">
                        <Stack gap={4} mb={4}>
                            <Flex
                                direction={{ base: 'column', md: 'row' }}
                                align={{ base: 'stretch', md: 'center' }}
                                gap={4}
                            >
                                <Heading size={{ base: '2xl', md: '3xl' }}>
                                    {contentType ? `${contentType.name} - Knowledge Base` : 'Knowledge Base'}
                                </Heading>
                                <Spacer />
                                {canModify && (
                                    <Button
                                        variant="outline"
                                        onClick={() => navigate(`/${workspaceId}/content-types`)}
                                        size={{ base: 'md', md: 'sm' }}
                                        width={{ base: 'full', md: 'auto' }}
                                    >
                                        <Icon as={Squares2X2Icon} w={4} h={4} />
                                        <Text display={{ base: 'none', sm: 'inline' }}>Content libraries</Text>
                                        <Text display={{ base: 'inline', sm: 'none' }}>Libraries</Text>
                                    </Button>
                                )}
                            </Flex>
                        </Stack>
                    </Container>
                </Card.Header>
                <Card.Body>
                    <Container maxW="4xl">
                        {/* Upload Section */}
                        {canModify && (
                            <Box mb={6}>
                                <form onSubmit={handleUploadSubmit}>
                                    <Stack gap={4}>
                                        <Text fontSize="lg" color="gray.700" fontWeight="semibold">
                                            Upload documents
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            Supported formats: PDF, Word, Excel, PowerPoint, text, CSV, Markdown, JSON
                                        </Text>
                                        <FileUpload.Root
                                            maxW="full"
                                            alignItems="stretch"
                                            maxFileSize={50 * 1024 * 1024}
                                            onFileAccept={handleUploadAccept}
                                            accept={{
                                                'application/pdf': ['.pdf'],
                                                'application/msword': ['.doc'],
                                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                                'application/vnd.ms-excel': ['.xls'],
                                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                                'application/vnd.ms-powerpoint': ['.ppt'],
                                                'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
                                                'text/plain': ['.txt'],
                                                'text/csv': ['.csv'],
                                                'text/markdown': ['.md'],
                                                'application/json': ['.json'],
                                            }}
                                        >
                                            <FileUpload.HiddenInput />
                                            <FileUpload.Dropzone minHeight="120px">
                                                <Icon as={ArrowUpTrayIcon} w={4} h={4} />
                                                <FileUpload.DropzoneContent>
                                                    <Box>Drag and drop files here</Box>
                                                    <Box color="fg.muted">Files up to 50MB each</Box>
                                                </FileUpload.DropzoneContent>
                                            </FileUpload.Dropzone>
                                            <FileUpload.List />
                                        </FileUpload.Root>
                                        <Flex gap={2}>
                                            <Button
                                                type="submit"
                                                colorPalette="brand"
                                                loading={isUploading}
                                                disabled={uploadFiles.length === 0}
                                            >
                                                Upload
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => setUploadFiles([])}
                                                disabled={uploadFiles.length === 0}
                                            >
                                                Clear
                                            </Button>
                                        </Flex>
                                    </Stack>
                                </form>
                            </Box>
                        )}

                        {/* Tabs */}
                        <Tabs.Root
                            value={activeTab}
                            onValueChange={(e) => setActiveTab(e.value)}
                            mb={4}
                        >
                            <Tabs.List>
                                <Tabs.Trigger value="documents">
                                    <Icon as={DocumentIcon} w={4} h={4} mr={2} />
                                    Documents ({documents.length})
                                </Tabs.Trigger>
                                <Tabs.Trigger value="gemini">
                                    <Icon as={Squares2X2Icon} w={4} h={4} mr={2} />
                                    Gemini Store
                                </Tabs.Trigger>
                            </Tabs.List>

                            {/* Documents Tab */}
                            <Tabs.Content value="documents">
                                <Flex justify="space-between" align="center" mt={4} mb={4}>
                                    <Box flex={1} mr={4}>
                                        <QuickSearchForm handleSearch={handleSearch} />
                                    </Box>
                                    <Tooltip content="Refresh documents">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={fetchDocuments}
                                            loading={isRefreshingDocuments}
                                        >
                                            <Icon as={ArrowPathIcon} w={4} h={4} />
                                            Refresh
                                        </Button>
                                    </Tooltip>
                                </Flex>

                                {filteredDocuments.length === 0 ? (
                                    <EmptyState.Root>
                                        <EmptyState.Content>
                                            <EmptyState.Indicator>
                                                <Icon as={DocumentIcon} w={8} h={8} />
                                            </EmptyState.Indicator>
                                            <EmptyState.Title>No documents</EmptyState.Title>
                                            <EmptyState.Description>
                                                Upload documents to your knowledge base to get started.
                                            </EmptyState.Description>
                                        </EmptyState.Content>
                                    </EmptyState.Root>
                                ) : (
                                    <>
                                        {/* Desktop Table */}
                                        <Box display={{ base: 'none', md: 'block' }}>
                                            <Table.Root>
                                                <Table.Header>
                                                    <Table.Row>
                                                        <Table.ColumnHeader>Document</Table.ColumnHeader>
                                                        <Table.ColumnHeader>Size</Table.ColumnHeader>
                                                        <Table.ColumnHeader>Status</Table.ColumnHeader>
                                                        <Table.ColumnHeader>Uploaded</Table.ColumnHeader>
                                                        <Table.ColumnHeader>Actions</Table.ColumnHeader>
                                                    </Table.Row>
                                                </Table.Header>
                                                <Table.Body>
                                                    {filteredDocuments.map((doc) => (
                                                        <DocumentTableRow
                                                            key={doc.id}
                                                            document={doc}
                                                            onDelete={handleDelete}
                                                            onRefreshStatus={handleRefreshStatus}
                                                            canDelete={canModify}
                                                            isRefreshing={refreshingDocId === doc.id}
                                                        />
                                                    ))}
                                                </Table.Body>
                                            </Table.Root>
                                        </Box>

                                        {/* Mobile Cards */}
                                        <Box display={{ base: 'block', md: 'none' }}>
                                            <Stack gap={3}>
                                                {filteredDocuments.map((doc) => (
                                                    <MobileDocumentCard
                                                        key={doc.id}
                                                        document={doc}
                                                        onDelete={handleDelete}
                                                        onRefreshStatus={handleRefreshStatus}
                                                        canDelete={canModify}
                                                        isRefreshing={refreshingDocId === doc.id}
                                                    />
                                                ))}
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                            </Tabs.Content>

                            {/* Gemini Store Tab */}
                            <Tabs.Content value="gemini">
                                <Box mt={4}>
                                    <Flex justify="space-between" align="center" mb={4}>
                                        <Text fontSize="sm" color="gray.500">
                                            Documents stored in Gemini File Search
                                        </Text>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={fetchGeminiDocuments}
                                            loading={isLoadingGemini}
                                        >
                                            <Icon as={ArrowPathIcon} w={4} h={4} />
                                            Refresh
                                        </Button>
                                    </Flex>

                                    {isLoadingGemini ? (
                                        <Flex justify="center" py={8}>
                                            <Spinner size="lg" />
                                        </Flex>
                                    ) : geminiStoreExists === false ? (
                                        <EmptyState.Root>
                                            <EmptyState.Content>
                                                <EmptyState.Indicator>
                                                    <Icon as={Squares2X2Icon} w={8} h={8} />
                                                </EmptyState.Indicator>
                                                <EmptyState.Title>No Gemini store created yet</EmptyState.Title>
                                                <EmptyState.Description>
                                                    A Gemini File Search store will be automatically created when you upload your first document.
                                                    Go to the Documents tab to upload files.
                                                </EmptyState.Description>
                                            </EmptyState.Content>
                                        </EmptyState.Root>
                                    ) : geminiDocuments.length === 0 ? (
                                        <EmptyState.Root>
                                            <EmptyState.Content>
                                                <EmptyState.Indicator>
                                                    <Icon as={Squares2X2Icon} w={8} h={8} />
                                                </EmptyState.Indicator>
                                                <EmptyState.Title>No Gemini documents</EmptyState.Title>
                                                <EmptyState.Description>
                                                    Documents will appear here once they are processed by Gemini.
                                                </EmptyState.Description>
                                            </EmptyState.Content>
                                        </EmptyState.Root>
                                    ) : (
                                        <>
                                            {/* Desktop Table */}
                                            <Box display={{ base: 'none', md: 'block' }}>
                                                <Table.Root>
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.ColumnHeader>Document</Table.ColumnHeader>
                                                            <Table.ColumnHeader>Size</Table.ColumnHeader>
                                                            <Table.ColumnHeader>State</Table.ColumnHeader>
                                                            <Table.ColumnHeader>Created</Table.ColumnHeader>
                                                            <Table.ColumnHeader>Actions</Table.ColumnHeader>
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {geminiDocuments.map((doc) => (
                                                            <GeminiDocumentTableRow
                                                                key={doc.name}
                                                                document={doc}
                                                                onDelete={handleGeminiDelete}
                                                                canDelete={canModify}
                                                            />
                                                        ))}
                                                    </Table.Body>
                                                </Table.Root>
                                            </Box>

                                            {/* Mobile Cards */}
                                            <Box display={{ base: 'block', md: 'none' }}>
                                                <Stack gap={3}>
                                                    {geminiDocuments.map((doc) => (
                                                        <MobileGeminiCard
                                                            key={doc.name}
                                                            document={doc}
                                                            onDelete={handleGeminiDelete}
                                                            canDelete={canModify}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Tabs.Content>
                        </Tabs.Root>
                    </Container>
                </Card.Body>
            </Card.Root>

            {/* Delete Document Modal */}
            {showDeleteModal && documentToDelete && (
                <DestructiveModal
                    isOpen={showDeleteModal}
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={onConfirmDelete}
                    isLoading={isDeleting}
                    title="Delete Document"
                    confirmText="Delete"
                >
                    {`You are about to delete "${documentToDelete.original_filename}". This will also remove it from Gemini. Press 'Delete' to continue.`}
                </DestructiveModal>
            )}

            {/* Delete Gemini Document Modal */}
            {showGeminiDeleteModal && geminiDocToDelete && (
                <DestructiveModal
                    isOpen={showGeminiDeleteModal}
                    onCancel={() => setShowGeminiDeleteModal(false)}
                    onConfirm={onConfirmGeminiDelete}
                    isLoading={isDeletingGemini}
                    title="Delete from Gemini"
                    confirmText="Delete"
                >
                    {`You are about to delete "${geminiDocToDelete.displayName || geminiDocToDelete.name?.split('/').pop()}" directly from Gemini File Search. This action cannot be undone. Press 'Delete' to continue.`}
                </DestructiveModal>
            )}
        </WorkspaceLayout>
    );
}

